/**
 * Highlights and notes on the meeting transcript (spec 05 cards 3-4, 6). Hydrated from the notes the
 * Worker inlines with the page (snapshot:notes:{meetingId}), saved optimistically to
 * /api/dashboard/notes and rolled back if the server refuses. Grouped by segment so a change re-renders
 * only that segment.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { Note } from "../types";
import { toastStore } from "./toastStore";
import { getViewer, signInToAct } from "../app/viewer";

export interface Draft {          // a selection being turned into a note ("Add note")
  segmentId: string;
  startChar: number;
  endChar: number;
  t: number;
  quote: string;
  speaker: string | null;
}

interface NotesState {
  meetingId: string;
  notes: Note[];
  bySegment: Record<string, Note[]>;
  composer: { segmentId: string; noteId: string | null; draft: Draft | null } | null;
  hydrate: (meetingId: string, notes: Note[]) => void;
  highlight: (d: Draft, color: Note["color"]) => Promise<void>;
  openComposer: (d: Draft) => void;
  editNote: (note: Note) => void;
  closeComposer: () => void;
  save: (text: string, visibility: Note["visibility"]) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const group = (notes: Note[]) => {
  const out: Record<string, Note[]> = {};
  for (const n of notes) (out[n.segmentId] ??= []).push(n);
  return out;
};

async function api(path: string, method: string, body?: unknown) {
  const res = await fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

export const notesStore = createStore<NotesState>()((set, get) => {
  const replace = (notes: Note[]) => set({ notes, bySegment: group(notes) });
  const create = async (d: Draft, color: Note["color"], text: string | null, visibility: Note["visibility"]) => {
    const temp: Note = {
      id: `tmp-${crypto.randomUUID()}`, meetingId: get().meetingId, segmentId: d.segmentId, range: { startChar: d.startChar, endChar: d.endChar },
      color, text: text ?? undefined, visibility, createdAt: new Date().toISOString(), t: d.t, quote: d.quote, speaker: d.speaker, mine: true,
    };
    replace([...get().notes, temp]);
    try {
      const saved = (await api("/api/dashboard/notes", "POST", {
        meetingId: get().meetingId, segmentId: d.segmentId, startChar: d.startChar, endChar: d.endChar, t: d.t, quote: d.quote, speaker: d.speaker, color, text, visibility,
      })) as Note;
      replace(get().notes.map((n) => (n.id === temp.id ? saved : n)));
    } catch {
      replace(get().notes.filter((n) => n.id !== temp.id));
      toastStore.getState().show({ message: "Couldn't save your note. Try again." });
    }
  };

  return {
    meetingId: "",
    notes: [],
    bySegment: {},
    composer: null,
    hydrate: (meetingId, notes) => set({ meetingId, notes, bySegment: group(notes), composer: null }),
    // The public meeting page: notes need dashboard access (sign in first).
    highlight: async (d, color) => { if (getViewer().admitted) await create(d, color, null, "private"); else signInToAct(); },
    openComposer: (d) => (getViewer().admitted ? set({ composer: { segmentId: d.segmentId, noteId: null, draft: d } }) : signInToAct()),
    editNote: (note) => set({ composer: { segmentId: note.segmentId, noteId: note.id, draft: null } }),
    closeComposer: () => set({ composer: null }),
    save: async (text, visibility) => {
      const c = get().composer;
      if (!c) return;
      set({ composer: null });
      if (c.draft) return create(c.draft, "yellow", text, visibility);
      const prev = get().notes;
      replace(prev.map((n) => (n.id === c.noteId ? { ...n, text, visibility } : n)));
      try {
        await api(`/api/dashboard/notes/${encodeURIComponent(c.noteId!)}`, "PATCH", { text, visibility });
      } catch {
        replace(prev);
        toastStore.getState().show({ message: "Couldn't save your note. Try again." });
      }
    },
    remove: async (id) => {
      const prev = get().notes;
      const note = prev.find((n) => n.id === id);
      if (!note) return;
      replace(prev.filter((n) => n.id !== id));
      try {
        await api(`/api/dashboard/notes/${encodeURIComponent(id)}`, "DELETE");
        toastStore.getState().show({
          message: "Note deleted",
          actionLabel: "Undo",
          onAction: () => {
            if (note.range && note.t != null && note.quote) {
              void create({ segmentId: note.segmentId, startChar: note.range.startChar, endChar: note.range.endChar, t: note.t, quote: note.quote, speaker: note.speaker ?? null },
                note.color, note.text ?? null, note.visibility);
            }
          },
        });
      } catch {
        replace(prev);
        toastStore.getState().show({ message: "Couldn't delete the note. Try again." });
      }
    },
  };
});

export const useNotes = <T,>(selector: (s: NotesState) => T) => useStore(notesStore, selector);
